import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useDebounce } from './useDebounce';
import { invalidateProgressNotesCache } from './useProgressNotes';
import {
    clearProgressNotes,
    clearProgressNotesSelection,
    clearProgressNotesSelectionError,
    patchProgressNotesSelectionPatient,
    patchProgressNotesSelectionPatients
} from '@/store/slices/patients';
import {
    bulkAssignProgressNotePatients,
    bulkUnassignProgressNotePatients,
    fetchProgressNotesSelectionPatients,
    fetchUnits
} from '@/store/slices/patients/patients-api';
import type { UnitOption } from '@/types/patient';
import type { FilterConfig, SortConfig } from './usePatients';
import { toast } from 'react-toastify';
import { registerSessionCacheInvalidator } from '@/services/auth-session';

interface UseProgressNotesPatientSelectionOptions {
    autoFetch?: boolean;
    unitRangeId?: string;
}

let lastLoadedSelectionQueryKey: string | null = null;
let lastLoadedSelectionUnitsHospitalId: string | null = null;

/** Clears in-memory reuse keys so the next selection listing mount refetches. */
export const invalidateProgressNotesSelectionCache = () => {
    lastLoadedSelectionQueryKey = null;
    lastLoadedSelectionUnitsHospitalId = null;
};

registerSessionCacheInvalidator(invalidateProgressNotesSelectionCache);

export const useProgressNotesPatientSelection = ({ autoFetch = true, unitRangeId }: UseProgressNotesPatientSelectionOptions = {}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { hospitalId: hospitalIdFromRoute } = useParams<{ hospitalId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsString = searchParams.toString();

    const {
        progressNotesSelectionPatients,
        units,
        progressNotesSelectionTotal,
        progressNotesSelectionServerPage,
        progressNotesSelectionServerLimit,
        progressNotesSelectionLoading,
        unitsLoading,
        progressNotesSelectionError,
        unitsError
    } = useAppSelector(({ patients }) => patients);
    const { currentHospitalId } = useAppSelector(({ hospital }) => hospital);
    const hospitalKey = hospitalIdFromRoute ?? currentHospitalId ?? null;
    const initialQueryState = useMemo(() => {
        const params = new URLSearchParams(searchParamsString);
        const initialPage = Number(params.get('page') ?? '1');
        const initialLimit = Number(params.get('limit') ?? '50');
        const initialSortOrder = params.get('sortOrder');

        return {
            search: params.get('search') ?? '',
            unit: params.get('unit'),
            tier: params.get('tier'),
            page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage - 1 : 0,
            limit: Number.isFinite(initialLimit) && initialLimit > 0 ? initialLimit : 50,
            sortBy: params.get('sortBy'),
            sortDirection: initialSortOrder === 'DESC' ? ('desc' as const) : ('asc' as const)
        };
    }, [searchParamsString]);

    const [inputValue, setInputValue] = useState(initialQueryState.search);
    const debouncedSearch = useDebounce(inputValue, 300);

    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: initialQueryState.sortBy ?? null,
        direction: initialQueryState.sortDirection
    });

    const [filterConfig, setFilterConfig] = useState<FilterConfig>({
        gender: null,
        unit: initialQueryState.unit ?? null,
        tier: initialQueryState.tier ?? null,
        readmitRisk: null,
        dcToday: null
    });

    const [page, setPage] = useState(initialQueryState.page);
    const [rowsPerPage, setRowsPerPage] = useState(initialQueryState.limit);
    const [checkedPatientIds, setCheckedPatientIds] = useState<Set<string>>(() => new Set());
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const activeRequestRef = useRef<{ abort?: () => void } | null>(null);
    const hasUserChangedRowsPerPageRef = useRef(false);
    const previousHospitalIdRef = useRef(hospitalKey);
    const isFirstSearchSyncRef = useRef(true);

    const loadUnits = useCallback(() => {
        dispatch(fetchUnits({ hospitalId: hospitalKey ?? undefined }));
    }, [dispatch, hospitalKey]);

    const selectedUnitId = useMemo(() => {
        if (!filterConfig.unit) return undefined;
        return units.find((unit) => unit.unitName === filterConfig.unit || unit.unitCode === filterConfig.unit)?.id;
    }, [filterConfig.unit, units]);

    const requestParams = useMemo(
        () => ({
            search: debouncedSearch.trim() || undefined,
            unitId: selectedUnitId,
            unitRangeId: unitRangeId || undefined,
            page: page + 1,
            limit: rowsPerPage,
            sortBy: sortConfig.key || undefined,
            sortOrder: sortConfig.key ? (sortConfig.direction === 'asc' ? ('ASC' as const) : ('DESC' as const)) : undefined,
            isSelected: null
        }),
        [debouncedSearch, selectedUnitId, unitRangeId, page, rowsPerPage, sortConfig.key, sortConfig.direction]
    );

    const queryKey = useMemo(
        () =>
            JSON.stringify({
                hospitalId: hospitalKey,
                endpoint: 'progress-notes-selection',
                ...requestParams
            }),
        [hospitalKey, requestParams]
    );

    const loadPatients = useCallback(() => {
        activeRequestRef.current?.abort?.();

        const requestPayload = { ...requestParams, hospitalId: hospitalKey ?? undefined };
        const request = dispatch(fetchProgressNotesSelectionPatients(requestPayload));
        lastLoadedSelectionQueryKey = queryKey;
        activeRequestRef.current = request;
        return request;
    }, [dispatch, requestParams, queryKey, hospitalKey]);

    const clearCheckedPatients = useCallback(() => {
        setCheckedPatientIds(new Set());
    }, []);

    const handleCheckedChange = useCallback((patientId: string, isChecked: boolean) => {
        setCheckedPatientIds((prev) => {
            const next = new Set(prev);
            if (isChecked) {
                next.add(patientId);
            } else {
                next.delete(patientId);
            }
            return next;
        });
    }, []);

    const handleBulkSelectionChange = useCallback(
        async (isSelected: boolean) => {
            const patientIds = Array.from(checkedPatientIds);
            if (patientIds.length === 0 || isBulkUpdating) {
                return;
            }

            const previousValues = new Map(
                patientIds.map((patientId) => {
                    const patient = progressNotesSelectionPatients.find((item) => item.id === patientId);
                    return [patientId, Boolean(patient?.isSelected)] as const;
                })
            );

            const requestPayload = { patientIds, hospitalId: hospitalKey ?? undefined };
            const bulkAction = isSelected ? bulkAssignProgressNotePatients : bulkUnassignProgressNotePatients;

            setIsBulkUpdating(true);
            dispatch(patchProgressNotesSelectionPatients({ patientIds, isSelected }));

            try {
                await dispatch(bulkAction(requestPayload)).unwrap();
                clearCheckedPatients();
                const patientLabel = patientIds.length === 1 ? 'Patient' : 'Patients';
                toast.success(isSelected ? `${patientLabel} assigned successfully` : `${patientLabel} unassigned successfully`);
                invalidateProgressNotesCache();
                dispatch(clearProgressNotes());
                const backPath = hospitalIdFromRoute ? `/admin/hospitals/${hospitalIdFromRoute}/my-patients` : '/my-patients';
                navigate(backPath, { replace: true });
            } catch (error) {
                previousValues.forEach((previousIsSelected, patientId) => {
                    dispatch(patchProgressNotesSelectionPatient({ patientId, isSelected: previousIsSelected }));
                });
                toast.error(error as string);
            } finally {
                setIsBulkUpdating(false);
            }
        },
        [
            checkedPatientIds,
            clearCheckedPatients,
            dispatch,
            hospitalIdFromRoute,
            hospitalKey,
            isBulkUpdating,
            navigate,
            progressNotesSelectionPatients
        ]
    );

    const clearAllPatients = useCallback(() => {
        dispatch(clearProgressNotesSelection());
    }, [dispatch]);

    const clearError = useCallback(() => {
        dispatch(clearProgressNotesSelectionError());
    }, [dispatch]);

    const handleSort = useCallback((key: string) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    const handleFilter = useCallback((key: keyof FilterConfig, value: string | null) => {
        let didChange = false;
        setFilterConfig((prev) => {
            if (prev[key] === value) {
                return prev;
            }
            didChange = true;
            return {
                ...prev,
                [key]: value
            };
        });
        if (didChange) {
            setPage((prev) => (prev === 0 ? prev : 0));
        }
    }, []);

    const handleChangePage = useCallback((newPage: number) => {
        setPage((prev) => (prev === newPage ? prev : newPage));
    }, []);

    const handleChangeRowsPerPage = useCallback((newRowsPerPage: number) => {
        hasUserChangedRowsPerPageRef.current = true;
        setRowsPerPage((prev) => (prev === newRowsPerPage ? prev : newRowsPerPage));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    const clearFilters = useCallback(() => {
        setFilterConfig((prev) => {
            if (!prev.gender && !prev.unit && !prev.tier && !prev.readmitRisk && !prev.dcToday) {
                return prev;
            }
            return {
                gender: null,
                unit: null,
                tier: null,
                readmitRisk: null,
                dcToday: null
            };
        });
        setInputValue((prev) => (prev === '' ? prev : ''));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    const processedPatients = useMemo(() => {
        let result = [...progressNotesSelectionPatients];

        if (filterConfig.gender) {
            result = result.filter((p) => p.gender === filterConfig.gender);
        }
        if (filterConfig.readmitRisk) {
            result = result.filter((p) => p.readmitRisk === filterConfig.readmitRisk);
        }
        if (filterConfig.dcToday) {
            result = result.filter((p) => p.dcToday === filterConfig.dcToday);
        }

        return result;
    }, [progressNotesSelectionPatients, filterConfig]);

    const hasClientOnlyFilters = Boolean(filterConfig.gender || filterConfig.readmitRisk || filterConfig.dcToday);
    const effectiveTotalCount = hasClientOnlyFilters ? processedPatients.length : progressNotesSelectionTotal;

    const handleSelectAllOnPage = useCallback(
        (isChecked: boolean) => {
            setCheckedPatientIds((prev) => {
                const next = new Set(prev);

                if (!isChecked) {
                    processedPatients.forEach((patient) => next.delete(patient.id));
                    return next;
                }

                processedPatients.forEach((patient) => next.add(patient.id));
                return next;
            });
        },
        [processedPatients]
    );

    const filterOptions = useMemo<{ units: string[] }>(() => {
        const mappedUnitOptions: string[] = units
            .map((unit: UnitOption) => unit.unitCode || unit.unitName)
            .filter((unit): unit is string => typeof unit === 'string' && unit.trim().length > 0);
        const unitsFromApi = [...new Set(mappedUnitOptions)].sort((a, b) => a.localeCompare(b));

        return {
            units: unitsFromApi
        };
    }, [units]);

    const unitOptions = useMemo<{ value: string; label: string }[]>(
        () => [{ value: 'all', label: 'All Units' }, ...filterOptions.units.map((unit) => ({ value: unit, label: unit }))],
        [filterOptions.units]
    );

    useEffect(() => {
        const shouldReuseUnits = units.length > 0 && lastLoadedSelectionUnitsHospitalId === hospitalKey;
        if (autoFetch && !shouldReuseUnits) {
            lastLoadedSelectionUnitsHospitalId = hospitalKey;
            loadUnits();
        }
    }, [autoFetch, loadUnits, units.length, hospitalKey]);

    useEffect(() => {
        if (
            !hasUserChangedRowsPerPageRef.current &&
            progressNotesSelectionServerLimit &&
            progressNotesSelectionServerLimit !== rowsPerPage
        ) {
            setRowsPerPage(progressNotesSelectionServerLimit);
        }
    }, [progressNotesSelectionServerLimit, rowsPerPage]);

    useEffect(() => {
        const shouldReuse = progressNotesSelectionPatients.length > 0 && lastLoadedSelectionQueryKey === queryKey;
        if (autoFetch && !shouldReuse) {
            loadPatients();
        }
    }, [autoFetch, loadPatients, progressNotesSelectionPatients.length, queryKey]);

    useEffect(() => {
        if (previousHospitalIdRef.current === hospitalKey) {
            return;
        }

        previousHospitalIdRef.current = hospitalKey;
        lastLoadedSelectionQueryKey = null;
        lastLoadedSelectionUnitsHospitalId = null;
        setFilterConfig((prev) => (prev.unit ? { ...prev, unit: null } : prev));
        setInputValue((prev) => (prev === '' ? prev : ''));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [hospitalKey]);

    useEffect(() => {
        if (isFirstSearchSyncRef.current) {
            isFirstSearchSyncRef.current = false;
            return;
        }
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [debouncedSearch]);

    useEffect(() => {
        clearCheckedPatients();
    }, [page, rowsPerPage, debouncedSearch, filterConfig.unit, hospitalKey, clearCheckedPatients]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParamsString);
        if (debouncedSearch.trim()) {
            nextParams.set('search', debouncedSearch.trim());
        } else {
            nextParams.delete('search');
        }
        if (filterConfig.unit) {
            nextParams.set('unit', filterConfig.unit);
        } else {
            nextParams.delete('unit');
        }
        nextParams.set('page', String(page + 1));
        nextParams.set('limit', String(rowsPerPage));

        if (sortConfig.key) {
            nextParams.set('sortBy', String(sortConfig.key));
            nextParams.set('sortOrder', sortConfig.direction === 'asc' ? 'ASC' : 'DESC');
        } else {
            nextParams.delete('sortBy');
            nextParams.delete('sortOrder');
        }

        const nextParamsString = nextParams.toString();
        if (nextParamsString !== searchParamsString) {
            setSearchParams(nextParams, { replace: true });
        }
    }, [debouncedSearch, filterConfig.unit, page, rowsPerPage, searchParamsString, setSearchParams, sortConfig.direction, sortConfig.key]);

    useEffect(
        () => () => {
            activeRequestRef.current?.abort?.();
        },
        []
    );

    return {
        patients: processedPatients,
        allPatients: progressNotesSelectionPatients,
        filteredCount: effectiveTotalCount,
        serverPage: progressNotesSelectionServerPage,
        serverLimit: progressNotesSelectionServerLimit,
        loading: progressNotesSelectionLoading,
        unitsLoading,
        error: progressNotesSelectionError,
        unitsError,
        inputValue,
        setInputValue,
        sortConfig,
        handleSort,
        filterConfig,
        filterOptions,
        unitOptions,
        handleFilter,
        clearFilters,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        checkedPatientIds,
        handleCheckedChange,
        handleSelectAllOnPage,
        clearCheckedPatients,
        handleBulkSelectionChange,
        isBulkUpdating,
        loadPatients,
        loadUnits,
        clearAllPatients,
        clearError
    };
};
